import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IonIcon } from '@ionic/react';
import {
  alertCircleOutline,
  trashOutline,
  warningOutline,
} from 'ionicons/icons';
import { ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { clearAuthSession } from '@shared/auth/session';
import { useCapacitorPlatform } from '@shared/hooks/useCapacitorPlatform';
import api from '../../lib/api';
import { useProToast } from './useProToast';
import ProCard from '../../components/pro/ProCard';
import ProPageModal from '../../components/pro/bottom-sheet/ProPageModal';
import ProActionButton from '../../components/pro/ProActionButton';
import './ProAccount.css';

/**
 * Self-service account deletion (Pro mobile).
 *
 * Flow (matches Profile hub → Delete account):
 *   1. Profile hub shows a first confirmation sheet ("Delete account?")
 *      and only navigates here on Continue.
 *   2. This page shows the warning + per-data-type impact and an inline
 *      "Continue to delete account" button at the bottom of content
 *      (no IonFooter — the button lives in the scrolling document).
 *   3. Tapping that button opens a final confirmation sheet that
 *      explains the sign-out + irreversibility and runs the mutation.
 *   4. On success: clearAuthSession + redirect to /pro/login.
 *
 * Backend:
 *   GET  /api/user-privacy/deletion-impact
 *   POST /api/user-privacy/delete-account { confirmPermanentDeletion: true }
 */

interface DataSummary {
  workspaces?: number;
  notes?: number;
  time_entries?: number;
}

interface DeleteAccountResponse {
  success: boolean;
  message: string;
  deactivatedOrgs: Array<{ id: string; name: string }>;
}

const ProProfileDeletePage: React.FC = () => {
  const { showSuccess, showError } = useProToast();
  const { triggerHaptic } = useCapacitorPlatform();
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);

  const { data: impact, isLoading: impactLoading } = useQuery<DataSummary>(
    ['deletion-impact'],
    async () => {
      const { data } = await api.get('/user-privacy/deletion-impact');
      return data.dataSummary ?? data.data?.dataSummary ?? data.dataSummary;
    },
  );

  const deleteMutation = useMutation<DeleteAccountResponse>(
    async () => {
      const { data } = await api.post('/user-privacy/delete-account', {
        confirmPermanentDeletion: true,
      });
      return data;
    },
    {
      onSuccess: (data) => {
        triggerHaptic(ImpactStyle.Heavy);
        if (data.deactivatedOrgs?.length > 0) {
          const orgNames = data.deactivatedOrgs.map((o) => o.name).join(', ');
          showSuccess(
            `Account deleted. Organizations deactivated: ${orgNames}.`,
          );
        } else {
          showSuccess('Account deleted.');
        }
        setTimeout(() => {
          clearAuthSession({ clearAccountType: true });
          window.location.href = Capacitor.isNativePlatform()
            ? '/pro/login'
            : '/login';
        }, 1400);
      },
      onError: (err: unknown) => {
        setShowFinalConfirm(false);
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data
            ?.error || 'Could not delete your account. Please try again.';
        showError(message);
      },
    },
  );

  const handleContinue = () => {
    triggerHaptic(ImpactStyle.Light);
    setShowFinalConfirm(true);
  };

  return (
    <>
      <div className="pro-account max-w-3xl mx-auto px-4 pt-4 pb-8">
        <div
          className="pro-account__group"
          role="region"
          aria-labelledby="delete-account-heading"
        >
          <ProCard className="border border-error-200 dark:border-error-800">
            <div className="p-5 text-center">
              <IonIcon
                icon={warningOutline}
                className="text-error-600 dark:text-error-400"
                style={{ fontSize: 44 }}
                aria-hidden="true"
              />
              <h2
                id="delete-account-heading"
                className="mt-3 mb-1.5 text-lg font-bold text-text-primary dark:text-text-dark-primary"
              >
                Delete your account
              </h2>
              <p className="text-sm leading-relaxed text-text-secondary dark:text-text-dark-secondary m-0">
                This <strong>permanently deletes</strong> your account immediately.
                There is no grace period and no recovery. Your sign-in and all
                active sessions will stop working right away.
              </p>
            </div>
          </ProCard>
        </div>

        {impact && (
          <div className="pro-account__group">
            <p className="pro-account__group-label">What will be removed</p>
            <ProCard>
              <ImpactRow
                label="Workspace memberships"
                count={impact.workspaces ?? 0}
              />
              <ImpactRow
                label="Notes you authored"
                count={impact.notes ?? 0}
                note="Shared notes stay; your name is removed from them."
              />
              <ImpactRow label="Time entries" count={impact.time_entries ?? 0} />
            </ProCard>
          </div>
        )}

        <ProActionButton
          variant="danger"
          icon={alertCircleOutline}
          loading={impactLoading}
          disabled={deleteMutation.isLoading}
          onClick={handleContinue}
          className="mt-4"
        >
          Continue to delete account
        </ProActionButton>
      </div>

      <ProPageModal
        isOpen={showFinalConfirm}
        onDismiss={() => {
          if (deleteMutation.isLoading) return;
          setShowFinalConfirm(false);
        }}
        title="Permanently delete account?"
      >
        <div className="px-4 py-5 space-y-4">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
            This cannot be undone. You&apos;ll be{' '}
            <strong>signed out immediately</strong> and won&apos;t be able to
            recover anything.
          </p>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary leading-relaxed">
            If you&apos;re the only admin of an organization, that organization
            will be deactivated for other members.
          </p>
          <div className="space-y-2">
            <ProActionButton
              variant="danger"
              icon={trashOutline}
              loading={deleteMutation.isLoading}
              onClick={() => deleteMutation.mutate()}
            >
              Permanently delete my account
            </ProActionButton>
            <ProActionButton
              variant="ghost"
              disabled={deleteMutation.isLoading}
              onClick={() => setShowFinalConfirm(false)}
            >
              Cancel
            </ProActionButton>
          </div>
        </div>
      </ProPageModal>
    </>
  );
};

interface ImpactRowProps {
  label: string;
  count: number;
  note?: string;
}

const ImpactRow: React.FC<ImpactRowProps> = ({ label, count, note }) => (
  // Untokenised pro gold accent: #ece4d1 / rgba(201,169,97) appear only here and
  // have no palette entry. Left as-is rather than repainted grey — that needs a
  // design decision, not a sweep.
  // eslint-disable-next-line no-restricted-syntax -- see above
  <div className="px-4 py-3.5 text-text-primary dark:text-text-dark-primary border-t border-[#ece4d1] dark:border-[rgba(201,169,97,0.12)] first:border-t-0">
    <div className="flex justify-between items-center gap-2 w-full">
      <span className="text-[15px] font-semibold leading-tight">{label}</span>
      <span
        className={`font-semibold text-base ${
          count > 0
            ? 'text-text-primary dark:text-text-dark-primary'
            : 'text-text-tertiary dark:text-text-dark-tertiary'
        }`}
      >
        {count}
      </span>
    </div>
    {note && count > 0 && (
      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 mb-0">
        {note}
      </p>
    )}
  </div>
);

export default ProProfileDeletePage;
